import type { Color } from '@xox/shared'
import { Crown } from 'lucide-react'
import type { ReactNode } from 'react'

/** One seat in a party game, as the lobby, podium and standings show it. */
export interface LineupEntry {
  id: string
  nickname: string
  color: Color
  isHost: boolean
  isMe: boolean
  cpu?: boolean
}

interface Props {
  entries: LineupEntry[]
  /** Seats that must be filled before the host can start. */
  min: number
  max: number
  /** The game's drawn figure for a seat (skater, light cycle…). */
  figure: (entry: LineupEntry) => ReactNode
  onDark?: boolean
  label?: string
}

/** Joined players as figures, plus dashed empty slots showing how many more can (or must) join. */
export function Lineup({ entries, min, max, figure, onDark = false, label = 'Players' }: Props) {
  const missing = Math.max(0, min - entries.length)
  const spare = max - entries.length - missing
  return (
    <ul className="flex flex-wrap items-end justify-center gap-2" aria-label={label}>
      {entries.map((e) => (
        <li key={e.id} className="flex flex-col items-center gap-0.5">
          {figure(e)}
          <span
            className={`max-w-16 truncate text-[10px] font-semibold ${onDark ? 'text-white' : ''} ${e.isMe ? 'underline decoration-2 decoration-sky-500' : ''}`}
          >
            {e.isHost && (
              <Crown className="mr-0.5 inline size-3 text-amber-500" aria-label="host" />
            )}
            {e.nickname}
          </span>
        </li>
      ))}
      {Array.from({ length: missing }, (_, i) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholders
          key={`need-${i}`}
          className="flex h-11 w-8 items-center justify-center rounded-xl border-2 border-dashed border-sky-400 text-base font-black text-sky-500"
          aria-label="Waiting for a player"
        >
          ?
        </li>
      ))}
      {spare > 0 && (
        <li
          className={`self-center text-xs ${onDark ? 'text-white/70' : 'text-slate-400'}`}
          aria-label={`${spare} more can join`}
        >
          +{spare}
        </li>
      )}
    </ul>
  )
}
